/**
 * CF月次推移(月×口座マトリクス・要件定義書v2.8 6-1-2 / モックv1.7準拠)。
 * 現行スプレッドシート③の置き換え。行=口座(グループ化+小計+全社合計)、列=年度12ヶ月。
 *
 * 実績(settled)のみを集計する。予定(planned)は資金繰り(CF)画面の残高見込みで扱う。
 * 口座に事業部の紐づけがまだ無いため、当面は会社ごとにグループ化する(要確認)。
 */
import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, getDocs, limit, query } from 'firebase/firestore';
import { getDb } from '@core/lib/firebase';
import { useMasters } from '@core/masters/MastersProvider';
import { useToast } from '@core/components/Toast';
import { fiscalYearOf, monthsOfFiscalYear, todayStr, toMonth } from '@core/lib/date';
import { formatYen } from '@core/lib/money';
import { fetchCashAccounts } from './cashRepo';
import { useIsMobile } from '@core/lib/useIsMobile';
import type { CashAccount, CashEntry } from '@core/lib/types';

type Cell = { in: number; out: number; balance: number; hasData: boolean };

export default function CashMatrixPage() {
  const { fiscal, companyName } = useMasters();
  const toast = useToast();
  const navigate = useNavigate();
  const [accounts, setAccounts] = useState<CashAccount[]>([]);
  const [entries, setEntries] = useState<CashEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState<'all' | 'bal'>('all');
  const [fiscalYear, setFiscalYear] = useState(() => fiscalYearOf(todayStr(), 4));
  /** スマホ(2026-09-07 指示): 12ヶ月×口座の表は横に収まらないため、月を選んで口座ごとに縦に並べる */
  const isMobile = useIsMobile();
  const [monthSel, setMonthSel] = useState(() => toMonth(todayStr()));

  useEffect(() => {
    setFiscalYear(fiscalYearOf(todayStr(), fiscal.start_month));
  }, [fiscal.start_month]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [accs, snap] = await Promise.all([
        fetchCashAccounts(),
        getDocs(query(collection(getDb(), 'cash_entries'), limit(5000))),
      ]);
      setAccounts(accs.filter((a) => a.active));
      setEntries(snap.docs.map((d) => ({ id: d.id, ...(d.data() as object) }) as CashEntry));
    } catch (e) {
      toast(e instanceof Error ? e.message : 'CF月次推移を取得できませんでした');
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    void load();
  }, [load]);

  const months = useMemo(
    () => monthsOfFiscalYear(fiscalYear, fiscal.start_month),
    [fiscalYear, fiscal.start_month],
  );

  /** 口座×月のセル(入金・出金・月末残高)。残高は年度前の分も含む累計 */
  const cells = useMemo(() => {
    const settled = entries.filter((e) => e.status === 'settled');
    const map = new Map<string, Cell[]>();
    for (const a of accounts) {
      const mine = settled.filter((e) => e.cash_account_id === a.id);
      const firstMonth = months[0] ?? '';
      // 年度開始前までの累計を期首として持つ
      let balance = a.opening_balance + mine
        .filter((e) => toMonth(e.date) < firstMonth)
        .reduce((s, e) => s + (e.direction === 'in' ? e.amount : -e.amount), 0);
      const row: Cell[] = months.map((m) => {
        const inMonth = mine.filter((e) => toMonth(e.date) === m);
        const inn = inMonth.filter((e) => e.direction === 'in').reduce((s, e) => s + e.amount, 0);
        const out = inMonth.filter((e) => e.direction === 'out').reduce((s, e) => s + e.amount, 0);
        balance += inn - out;
        return { in: inn, out, balance, hasData: inMonth.length > 0 };
      });
      map.set(a.id, row);
    }
    return map;
  }, [accounts, entries, months]);

  const groups = useMemo(() => {
    const byCompany = new Map<string, CashAccount[]>();
    for (const a of accounts) {
      const key = a.company_id ?? 'sum';
      byCompany.set(key, [...(byCompany.get(key) ?? []), a]);
    }
    return [...byCompany.entries()];
  }, [accounts]);

  function sumCells(list: CashAccount[]): Cell[] {
    return months.map((_, i) =>
      list.reduce<Cell>(
        (acc, a) => {
          const c = cells.get(a.id)?.[i];
          if (!c) return acc;
          return {
            in: acc.in + c.in,
            out: acc.out + c.out,
            balance: acc.balance + c.balance,
            hasData: acc.hasData || c.hasData,
          };
        },
        { in: 0, out: 0, balance: 0, hasData: false },
      ),
    );
  }

  const totals = useMemo(() => sumCells(accounts), [accounts, cells, months]);

  // スマホで表示する月。年度を切り替えたら今月→その年度の最後の月の順で選び直す
  const currentMonth = toMonth(todayStr());
  const month = months.includes(monthSel)
    ? monthSel
    : months.includes(currentMonth)
      ? currentMonth
      : (months[months.length - 1] ?? '');
  const monthIndex = Math.max(0, months.indexOf(month));

  function exportCsv() {
    const head = ['口座'];
    for (const m of months) {
      const label = `${Number(m.slice(5))}月`;
      if (mode === 'bal') head.push(`${label}残高`);
      else head.push(`${label}入金`, `${label}出金`, `${label}残高`);
    }
    const lines = [head.join(',')];
    const pushRow = (name: string, row: Cell[]) => {
      const cols = [name];
      for (const c of row) {
        if (mode !== 'bal') cols.push(String(c.in), String(c.out));
        cols.push(String(c.balance));
      }
      lines.push(cols.join(','));
    };
    for (const [companyId, list] of groups) {
      for (const a of list) pushRow(`${companyName(companyId)}:${a.name}`, cells.get(a.id) ?? []);
      pushRow(`${companyName(companyId)} 小計`, sumCells(list));
    }
    pushRow('全社合計', totals);
    const blob = new Blob(['﻿' + lines.join('\r\n')], { type: 'text/csv;charset=utf-8' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `CF月次推移_${fiscalYear}年度.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  }

  function renderRow(name: string, row: Cell[], opts: { strong?: boolean; indent?: boolean; navy?: boolean; clickable?: boolean } = {}) {
    return (
      <tr
        key={name}
        style={opts.navy ? { background: 'var(--navy)', color: '#fff' } : opts.strong ? { background: '#FAFBF9' } : undefined}
      >
        <td style={{ paddingLeft: opts.indent ? 22 : 10, fontWeight: opts.strong || opts.navy ? 700 : 400, color: opts.navy ? '#fff' : undefined }}>
          {opts.clickable ? (
            <button className="txn-link" style={{ padding: 0 }} onClick={() => navigate('/cf')}>
              {name}
            </button>
          ) : (
            name
          )}
        </td>
        {row.map((c, i) => {
          const z = !c.hasData && c.in === 0 && c.out === 0;
          return (
            <MonthCells key={months[i]} c={c} z={z} mode={mode} navy={opts.navy} strong={opts.strong || opts.navy} />
          );
        })}
      </tr>
    );
  }

  /** スマホ用: 選んだ月の1口座分(名称・入金・出金・残高)。CSSで縦積みカードになる */
  function renderMonthRow(name: string, c: Cell | undefined, opts: { strong?: boolean; navy?: boolean; clickable?: boolean } = {}) {
    const cell = c ?? { in: 0, out: 0, balance: 0, hasData: false };
    const z = !cell.hasData && cell.in === 0 && cell.out === 0;
    const color = opts.navy ? '#fff' : undefined;
    return (
      <tr
        key={name}
        className={opts.navy ? 'row-navy' : undefined}
        style={opts.navy ? { background: 'var(--navy)', color: '#fff', borderColor: 'var(--navy)' } : opts.strong ? { background: '#FAFBF9' } : undefined}
      >
        <td style={{ fontWeight: opts.strong || opts.navy ? 700 : 600, color }}>
          {opts.clickable ? (
            <button className="txn-link" style={{ padding: 0 }} onClick={() => navigate('/cf')}>
              {name}
            </button>
          ) : (
            name
          )}
        </td>
        {mode !== 'bal' && (
          <>
            <td className="num" style={{ color }}>
              {z ? '—' : formatYen(cell.in)}
            </td>
            <td className="num" style={{ color }}>
              {z ? '—' : formatYen(cell.out)}
            </td>
          </>
        )}
        <td className="num" style={{ fontWeight: 700, color: z ? (opts.navy ? '#9FB0BE' : 'var(--muted)') : color }}>
          {z ? '—' : formatYen(cell.balance)}
        </td>
      </tr>
    );
  }

  const monthHeadRow = (
    <tr>
      <th>口座</th>
      {mode !== 'bal' && (
        <>
          <th className="num">入金</th>
          <th className="num">出金</th>
        </>
      )}
      <th className="num">残高</th>
    </tr>
  );

  return (
    <section className="screen" style={{ maxWidth: 1500 }}>
      <div className="card" style={{ padding: '20px 18px' }}>
        <h3 style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          現預金 月次推移({fiscalYear}年度)
          <span className="hint" style={{ fontWeight: 400 }}>
            — 口座×月の入金・出金・残高(実績のみ)
          </span>
          <span className="period" style={{ marginLeft: 'auto' }}>
            <button className={mode === 'all' ? 'on' : ''} onClick={() => setMode('all')}>
              入金・出金・残高
            </button>
            <button className={mode === 'bal' ? 'on' : ''} onClick={() => setMode('bal')}>
              残高のみ
            </button>
          </span>
          <select
            style={{ padding: '6px 10px', border: '1px solid var(--line)', borderRadius: 6, fontFamily: 'inherit', fontSize: 13 }}
            value={fiscalYear}
            onChange={(e) => setFiscalYear(Number(e.target.value))}
          >
            {[fiscalYear - 1, fiscalYear, fiscalYear + 1].map((y) => (
              <option key={y} value={y}>
                {y}年度
              </option>
            ))}
          </select>
          <button className="btn-line" onClick={exportCsv}>
            CSV出力
          </button>
        </h3>

        {loading ? (
          <p className="empty">読み込み中…</p>
        ) : accounts.length === 0 ? (
          <p className="empty">口座が登録されていません。</p>
        ) : isMobile ? (
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
              <span className="hint">表示する月</span>
              <select
                style={{ padding: '6px 10px', border: '1px solid var(--line)', borderRadius: 6, fontFamily: 'inherit' }}
                value={month}
                onChange={(e) => setMonthSel(e.target.value)}
              >
                {months.map((m) => (
                  <option key={m} value={m}>
                    {m.slice(0, 4)}年{Number(m.slice(5))}月
                  </option>
                ))}
              </select>
            </div>
            {groups.map(([companyId, list]) => (
              <div key={companyId} style={{ marginBottom: 12 }}>
                <div style={{ fontWeight: 700, fontSize: 13, margin: '8px 0 6px' }}>{companyName(companyId)}</div>
                <table>
                  <thead>{monthHeadRow}</thead>
                  <tbody>
                    {list.map((a) => renderMonthRow(a.name, cells.get(a.id)?.[monthIndex], { clickable: true }))}
                    {groups.length > 1 &&
                      renderMonthRow(`${companyName(companyId)} 小計`, sumCells(list)[monthIndex], { strong: true })}
                  </tbody>
                </table>
              </div>
            ))}
            <table>
              <thead>{monthHeadRow}</thead>
              <tbody>{renderMonthRow('全社合計', totals[monthIndex], { navy: true })}</tbody>
            </table>
          </div>
        ) : (
          <div>
            <table className="tx-table">
              <thead>
                <tr>
                  <th rowSpan={2} style={{ minWidth: 170 }}>
                    口座
                  </th>
                  {months.map((m) => (
                    <th
                      key={m}
                      colSpan={mode === 'bal' ? 1 : 3}
                      style={{ textAlign: 'center', borderBottom: '1px solid var(--line)' }}
                    >
                      {Number(m.slice(5))}月
                    </th>
                  ))}
                </tr>
                <tr>
                  {months.map((m) =>
                    mode === 'bal' ? (
                      <th key={m} className="num">
                        残高
                      </th>
                    ) : (
                      <MonthHead key={m} />
                    ),
                  )}
                </tr>
              </thead>
              <tbody>
                {groups.map(([companyId, list]) => (
                  <Fragment key={companyId}>
                    <tr key={`g-${companyId}`}>
                      <td
                        colSpan={1 + months.length * (mode === 'bal' ? 1 : 3)}
                        style={{ background: '#F0F2EE', fontWeight: 700, padding: '7px 10px' }}
                      >
                        {companyName(companyId)}
                      </td>
                    </tr>
                    {list.map((a) => renderRow(a.name, cells.get(a.id) ?? [], { indent: true, clickable: true }))}
                    {groups.length > 1 &&
                      renderRow(`${companyName(companyId)} 小計`, sumCells(list), { strong: true, indent: true })}
                  </Fragment>
                ))}
                {renderRow('全社合計', totals, { navy: true })}
              </tbody>
            </table>
          </div>
        )}

        <p className="hint" style={{ marginTop: 10 }}>
          実績(確定した入出金)のみを集計しています。予定を含む残高見込みは資金繰り(CF)画面で確認できます。
          口座名をクリックすると入出金明細へ移動します。
          ※口座への事業部の紐づけは未設定のため、当面は会社ごとの小計です。
        </p>
      </div>
    </section>
  );
}

function MonthHead() {
  return (
    <>
      <th className="num">入金</th>
      <th className="num">出金</th>
      <th className="num">残高</th>
    </>
  );
}

function MonthCells({
  c,
  z,
  mode,
  navy,
  strong,
}: {
  c: Cell;
  z: boolean;
  mode: 'all' | 'bal';
  navy?: boolean;
  strong?: boolean;
}) {
  const color = navy ? '#fff' : undefined;
  const style = { color } as const;
  return (
    <>
      {mode !== 'bal' && (
        <>
          <td className="num" style={style}>
            {z ? '—' : formatYen(c.in)}
          </td>
          <td className="num" style={style}>
            {z ? '—' : formatYen(c.out)}
          </td>
        </>
      )}
      <td className="num" style={{ fontWeight: strong ? 700 : 600, color: z ? (navy ? '#9FB0BE' : 'var(--muted)') : color }}>
        {z ? '—' : formatYen(c.balance)}
      </td>
    </>
  );
}
